import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SessionConflictDialog from '../components/SessionConflictDialog';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    username: '',
    password: '',
  });
  const [touched, setTouched] = useState({
    username: false,
    password: false,
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Session conflict dialog state
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [activeSessionInfo, setActiveSessionInfo] = useState(null);

  const navigate = useNavigate();
  const { login } = useAuth();

  // Load remembered username on component mount
  useEffect(() => {
    const rememberedUsername = localStorage.getItem('remembered_username');
    if (rememberedUsername) {
      setFormData(prev => ({
        ...prev,
        username: rememberedUsername
      }));
      setRememberMe(true);
    }
  }, []);

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'username':
        if (!value.trim()) {
          error = 'Username is required';
        } else if (value.length < 3) {
          error = 'Username must be at least 3 characters';
        }
        break;
      case 'password':
        if (!value) {
          error = 'Password is required';
        } else if (value.length < 6) {
          error = 'Password must be at least 6 characters';
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate field if it has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setFieldErrors({
        ...fieldErrors,
        [name]: error,
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });

    const error = validateField(name, value);
    setFieldErrors({
      ...fieldErrors,
      [name]: error,
    });
  };

  const handleSubmit = async (e, forceLogout = false) => {
    e.preventDefault();
    setError('');

    // Mark all fields as touched
    setTouched({
      username: true,
      password: true,
    });

    // Validate all fields
    const usernameError = validateField('username', formData.username);
    const passwordError = validateField('password', formData.password);

    setFieldErrors({
      username: usernameError,
      password: passwordError,
    });

    // If there are validation errors, don't submit
    if (usernameError || passwordError) {
      return;
    }

    setLoading(true);

    try {
      const response = await login(formData, forceLogout);

      // Handle Remember Me
      if (rememberMe) {
        localStorage.setItem('remembered_username', formData.username);
      } else {
        localStorage.removeItem('remembered_username');
      }

      // Redirect based on role
      if (response.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (err) {
      // Check if it's a session conflict (409 status)
      if (err.response?.status === 409 && err.response?.data?.session_conflict) {
        // Show session conflict dialog
        setActiveSessionInfo(err.response.data.active_session);
        setShowSessionDialog(true);
      } else {
        setError(err.response?.data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSessionConflictConfirm = async () => {
    setShowSessionDialog(false);
    // Retry login with force_logout=true
    const fakeEvent = { preventDefault: () => {} };
    await handleSubmit(fakeEvent, true);
  };

  const handleSessionConflictCancel = () => {
    setShowSessionDialog(false);
    setActiveSessionInfo(null);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl bg-white shadow-[0_2px_10px_-3px_rgba(6,81,237,0.3)] rounded-md overflow-hidden">
        <div className="grid md:grid-cols-2 gap-0">
          {/* Form Section */}
          <div className="p-6 md:p-8 lg:p-12">
            <form className="max-w-md mx-auto w-full" onSubmit={handleSubmit}>
              <div className="mb-8">
                <div className="mb-6">
                  <img src="/Logo_png_file-removebg-preview.png" alt="LearnersEdge Logo" className="h-32 w-auto mx-auto my-0" />
                </div>
                <h2 className="text-slate-900 text-2xl md:text-3xl font-semibold">Sign In</h2>
                <p className="text-slate-600 text-sm mt-2">Welcome back! Please sign in to your account</p>
              </div>

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-6">
                  {error}
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="text-slate-900 text-sm font-medium mb-2 block">Username</label>
                  <div className="relative flex items-center">
                    <input
                      name="username"
                      type="text"
                      value={formData.username}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full text-sm text-slate-900 bg-slate-100 focus:bg-transparent pl-4 pr-10 py-3 rounded-md border outline-none transition-all ${
                        fieldErrors.username && touched.username
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-slate-100 focus:border-blue-600'
                      }`}
                      placeholder="Enter username"
                    />
                    <svg xmlns="http://www.w3.org/2000/svg" fill="#bbb" stroke="#bbb" className="w-[18px] h-[18px] absolute right-4" viewBox="0 0 24 24">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                  {fieldErrors.username && touched.username && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.username}</p>
                  )}
                </div>

                <div>
                  <label className="text-slate-900 text-sm font-medium mb-2 block">Password</label>
                  <div className="relative flex items-center">
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full text-sm text-slate-900 bg-slate-100 focus:bg-transparent pl-4 pr-10 py-3 rounded-md border outline-none transition-all ${
                        fieldErrors.password && touched.password
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-slate-100 focus:border-blue-600'
                      }`}
                      placeholder="Enter password"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="#bbb"
                      stroke="#bbb"
                      className="w-[18px] h-[18px] absolute right-4 cursor-pointer"
                      viewBox="0 0 128 128"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <path d="M64 104C22.127 104 1.367 67.496.504 65.943a4 4 0 0 1 0-3.887C1.367 60.504 22.127 24 64 24s62.633 36.504 63.496 38.057a4 4 0 0 1 0 3.887C126.633 67.496 105.873 104 64 104zM8.707 63.994C13.465 71.205 32.146 96 64 96c31.955 0 50.553-24.775 55.293-31.994C114.535 56.795 95.854 32 64 32 32.045 32 13.447 56.775 8.707 63.994zM64 88c-13.234 0-24-10.766-24-24s10.766-24 24-24 24 10.766 24 24-10.766 24-24 24zm0-40c-8.822 0-16 7.178-16 16s7.178 16 16 16 16-7.178 16-16-7.178-16-16-16z" />
                    </svg>
                  </div>
                  {fieldErrors.password && touched.password && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.password}</p>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-4 justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="shrink-0 h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded-md cursor-pointer"
                    />
                    <label htmlFor="remember-me" className="ml-3 block text-sm text-slate-900 cursor-pointer">
                      Remember me
                    </label>
                  </div>
                  <div className="text-sm">
                    <Link to="/" className="text-blue-600 font-medium hover:underline">
                      Back to Home
                    </Link>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full shadow-xl py-3 px-4 text-[15px] tracking-wide font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
                <p className="text-sm mt-6 text-center text-slate-600">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-blue-600 font-medium tracking-wide hover:underline ml-1">
                    Register here
                  </Link>
                </p>
              </div>
            </form>
          </div>

          {/* Image/Info Section - Hidden on mobile, visible on md and up */}
          <div className="hidden md:flex relative bg-gradient-to-br from-blue-50 to-blue-100 min-h-[400px] lg:min-h-[600px]">
            <div className="absolute inset-0 bg-blue-600/70"></div>
            <div className="relative z-10 flex items-center justify-center p-8 lg:p-12 w-full">
              <div className="text-center max-w-md">
                <h1 className="text-white text-3xl lg:text-4xl font-semibold">Welcome Back!</h1>
                <p className="text-slate-100 text-sm lg:text-[15px] font-medium mt-6 leading-relaxed">
                  Sign in to your account and continue your learning journey. Access your courses, track your progress, and achieve your goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Session Conflict Dialog */}
      <SessionConflictDialog
        isOpen={showSessionDialog}
        activeSession={activeSessionInfo}
        onConfirm={handleSessionConflictConfirm}
        onCancel={handleSessionConflictCancel}
      />
    </div>
  );
};

export default Login;
